import { UploadedFile } from 'app/features/Module/ModuleCreate/ModuleAI/ModuleAIFiles';
import { IAction, IActionDAO } from './action';
import { IBaseEntity } from './base';
import { ICourseNode } from './course';
import { IGroup } from './group';
import { IImageDAO } from './image';
import { ISceneDAO } from './scene';
import { ISessionAttempt } from './session';
import { IText, ITextDAO } from './text';
import { IVariableDAO } from './variable';

export interface IModule extends IBaseEntity {
  name: string;
  organisationId: number;
  moduleConfigId: number;
  textId: number;
  imageId: number;
  originalId: number;
  variableId: number;
  aiModuleId: number;
  forumId: number;
  folderId: number;
  parentFolderId: number;
  actionId: number;
  uploadId?: string;
  type: ModuleType;
  status: ModuleStatus;
  url: string;
  __stage: number;
  __stale: boolean;
  actions: IAction[];
  stage: ModuleStage;
  assess?: Record<string, unknown>;
  public: boolean;
  forumTopicCount?: number;
  /**
   * Slides in the module, where the payload counted them instead of sending them.
   *
   * `get COU` and `list MOD` carry this rather than every action: the admin only ever
   * renders the number, and shipping the slides (with their text) to count them was
   * over half the bytes of a course. Undefined where the payload did send the actions
   * — the module editor's `get MOD` — so a caller falls back to the ACTION bucket,
   * which is then the live count as the author adds and removes slides.
   */
  actionCount?: number;
  value?: number;
  statsDuration?: number;
}

export interface IModuleConfig extends IBaseEntity {
  moduleId: number;
  moduleInstanceId: number;
  languageId: number;
  maxValue: number;
  minValue: number;
  scormVersion: string;
}

export interface IModuleDAO extends IModule {
  action: IAction;
  text: IText;
  image: IImageDAO;
  outs: IModuleOut[];
  actions: IAction[];
  scenes: ISceneDAO[];
  variable: IVariableDAO;
  clonedVersion?: IModuleVersion;
  versions?: IModuleVersion[];
  //tags: ITagJoin[];
  courseNode?: ICourseNode;
  courseName?: string;
}

export interface IModuleFlowNode {
  id: string;
  depth: number;
  data: Record<string, unknown>;
  children: IModuleFlowNode[];
  index: number;
  source: string;
  sourceHandle: string;
  height: number;
  position: { x: number; y: number };
}

export interface IModuleDetailDAO extends IModuleDAO {
  outs: IModuleOut[];
  actions: IActionDAO[];
  courseNodes: ICourseNode[];
}

export interface IModuleBasicDAO extends IModule {
  sessionAttemptId?: number;
  name: string;
  text: ITextDAO;
  image: IImageDAO;
  type: ModuleType;
  status: ModuleStatus;
  //tags: ITagJoinDAO[];
}

export interface IModuleAssign extends IModuleBasicDAO {
  modifiedDate: string;
  canContinue: boolean;
  sessionAttempt: ISessionAttempt;
  startActionId: number;
  sessionAttemptId: number;
  suspendData: string;
  order: number[];
  /** The course node's question limit — a random-order quiz serves this many. */
  limit?: number;
}

export enum ModuleType {
  STANDARD = 0,
  PPT = 1,
  PDF = 2,
  TEXT = 3,
  YOUTUBE = 4,
  WEBSITE = 5,
  SCORM = 6,
  VR = 7,
  AR = 8,
  MAIL = 9,
  LINEAR = 10,
  ASSESSMENT = 11,
  SCENARIO = 12,
  THREE60 = 13,
  INTERACTIVE_VIDEO = 14,
  THREED_MODEL = 15,
  HOTSPOTS = 16,
  CHALLENGE = 17,
  AI = 18,
  ASSESSMENT_RANDOM = 19,
  INTERACTIVE_IMAGE = 20,
}

export enum ModuleStage {
  SHALLOW = 0,
  DEEP = 1,
}

export enum ModuleStatus {
  DELETED = -1,
  ACTIVE = 0,
  PROCESSING = 1,
  ERROR_PROCESSING = 2,
  NEEDS_PUBLISHING = 3,
  TEMPLATE = 4,
}

export interface IModuleOut extends IBaseEntity {
  moduleId: number;
  sourceId: number;
  sourceHandle: number;
  targetId: number;
  targetHandle: number;
}

export interface IModuleVersion extends IBaseEntity {
  moduleId: number;
  parentModuleId: number;
  jsonUrl: string;
  name: string;
  groupId: number;
}

export interface IModuleVersionDAO extends IModuleVersion {
  group: IGroup;
}

export interface IAIModule {
  id?: number;
  /**
   * The module being generated into. Sent so a generation can be scoped to its
   * target while in flight; the server backfills it on the response from the AI
   * module row, which is where it is authoritative.
   */
  moduleId?: number;
  stage: number;
  courseId: number;
  topic: string;
  type: number;
  audience: string;
  expertiseLevel: string;
  moduleLength: string;
  files: UploadedFile[];
  urls: string[];
}
